<template>
  <div class="sm-form">
    <h3>Create location</h3>
    <div>
      <div class="sm-form__locations-stepper">
        <span
          v-for="step in [0, 1, 2]"
          :key="step"
          class="step"
          :class="{ active: activeStep >= step }"
          @click="goToStep(step, step < activeStep ? false : true)"
        ></span>
      </div>
      <form class="sm-form sm-form__locations" @submit.prevent="handleSubmit">
        <div v-show="activeStep === 0" class="sm-form__locations-section">
          <h4>General business information</h4>
          <div>
            <input-text
              v-model="formData.client.name"
              v-validate="'required'"
              data-vv-as="'Company name'"
              data-vv-scope="0"
              :label="'Company name'"
              :name="'name'"
              :error="
                errors.first('0.name') || errors.first('0.backError_name') || ''
              "
              :showLoader="isValidating.name"
              @blur="validateDB('client', 'name', '0')"
            />
            <input-text
              v-model="formData.client.subdomain"
              v-validate="'required|sm_lower_alpha_num_dash|min:3'"
              data-vv-as="'Subdomain'"
              data-vv-scope="0"
              :label="'Subdomain'"
              :name="'subdomain'"
              :error="
                errors.first('0.subdomain') ||
                errors.first('0.backError_subdomain') ||
                ''
              "
              :showLoader="isValidating.subdomain"
              @blur="validateDB('client', 'subdomain', '0')"
            />
            <input-text
              v-model="formData.client.url"
              v-validate="'required|url'"
              data-vv-as="'Business URL'"
              data-vv-scope="0"
              :label="'Business URL'"
              :name="'business_url'"
              :error="errors.first('0.business_url')"
            />
            <div class="sm-form__regist-block">
              <phone-input
                v-model="formData.common.phone"
                v-validate="'required'"
                data-vv-as="'Phone'"
                data-vv-scope="1"
                :start_val="formData.common.phone"
                :label="'Phone'"
                :show_errors="true"
                :error_text="
                  errors.first('0.manual_phone') ||
                  errors.first('0.phone') ||
                  ''
                "
                class="sm-form__regist-label"
              />
            </div>
            <input-text
              v-model="formData.common.email"
              :class="'mobile-full'"
              :label="'Business email'"
              :name="'email'"
              v-validate="'required|email'"
              data-vv-as="'Business email'"
              data-vv-scope="0"
              :error="
                errors.first('0.email') ||
                errors.first('0.backError_email') ||
                ''
              "
              :showLoader="isValidating.email"
              :isEmail="true"
              @blur="validateDB('common', 'email', '0')"
            />
            <div class="sm-form__notarize-wrap">
              <div
                class="sm-form__notarize-upload"
                :class="{ error: errors.first('0.image') }"
              >
                <template v-if="formData.client.image">
                  <include-svg :name="'icon-document'" />
                  <div class="sm-form__notarize-upload-text">
                    <p class="sm-h6">{{ imageName }}</p>
                  </div>
                  <a
                    href="#"
                    class="sm-error"
                    @click.prevent="formData.client.image = ''"
                    >Delete</a
                  >
                </template>
                <template v-else>
                  <include-svg :name="'icon-document'" />
                  <div class="sm-form__notarize-upload-text">
                    <p class="sm-h6">Company logo</p>
                    <p class="sm-small sm-grey-add">gif, jpeg, jpg or png</p>
                  </div>
                  <btn-block
                    @click="$refs.image.click()"
                    :type="'upload'"
                    :text="'Upload'"
                    :icon="'icon-upload'"
                  />
                  <input
                    ref="image"
                    hidden
                    type="file"
                    name="image"
                    :accept="acceptTypes"
                    @change="uploadFile($event, 'file')"
                  />
                </template>
              </div>
              <span
                v-if="errors.first('0.image')"
                class="sm-input-text-error"
                >{{ errors.first("0.image") }}</span
              >
            </div>
          </div>
        </div>

        <div v-show="activeStep === 1" class="sm-form__locations-section">
          <h4>Business address</h4>

          <input-text
            v-model="formData.common.street"
            data-vv-as="'Street'"
            data-vv-scope="1"
            :label="`Street`"
            :name="'street'"
            v-validate="`required`"
            :autocomplete="`street-address`"
            :error="errors.first('1.street')"
          />
          <div class="sm-form__regist-flex" style="margin-top: 0">
            <input-text
              v-model="formData.common.city"
              data-vv-as="'City'"
              data-vv-scope="1"
              :label="`City`"
              :name="'city'"
              v-validate="`required`"
              :autocomplete="`address-level2`"
              :error="errors.first('1.city')"
            />
            <input-zip
              v-model="formData.common.zip_code"
              v-validate="'required|is_zip'"
              data-vv-as="'Zip code'"
              data-vv-scope="1"
              :country="selectedCountry"
              :name="'zip_code'"
              :error="errors.first('1.zip_code')"
            />
          </div>
          <div style="margin-top: 12px">
            <country-dropdown
              v-model="selectedCountry"
              data-vv-as="'Country'"
              data-vv-scope="1"
              :class="'sm-form__regist-label'"
              :countryList="countryList"
              :startVal="selectedCountry"
              :error="errors.first('1.country_id')"
            />
          </div>
          <div
            class=""
            :class="{
              'no-state': !stateList.length,
            }"
            style="margin-top: 12px"
          >
            <state-dropdown
              v-if="stateList.length > 0"
              v-model="selectedState"
              data-vv-scope="1"
              :class="'sm-form__regist-label'"
              :stateList="stateList"
              :startVal="selectedState"
              :error="errors.first('1.state_id')"
            />
          </div>
          <div style="margin-top: 12px">
            <dropdown-wrap
              v-model="currency"
              :val="currency"
              :options="currencyList"
              :placeholder="'Select currency...'"
              :label="'Currency'"
            />
          </div>
        </div>
        <div v-show="activeStep === 2" class="sm-form__locations-section">
          <h4>Account information</h4>
          <input-text
            v-model="formData.account.user_name"
            :class="'username'"
            :label="`Username`"
            :name="`user_name`"
            :autocomplete="`username`"
            v-validate="`required|min:6|alpha_num`"
            data-vv-as="Username"
            data-vv-scope="2"
            :error="
              errors.first('2.user_name') ||
              errors.first('2.backError_user_name') ||
              ''
            "
            :showLoader="isValidating.user_name"
            @blur="validateDB('account', 'user_name', '2')"
          />
          <div class="sm-form__regist-flex">
            <input-text
              v-model="formData.account.first_name"
              :label="`First name`"
              :name="'first_name'"
              :autocomplete="`given-name`"
              v-validate="'required|alpha|min:2|max:100'"
              data-vv-as="First name"
              data-vv-scope="2"
              :error="errors.first('2.first_name')"
            />
            <input-text
              v-model="formData.account.last_name"
              :label="`Last name`"
              :name="'last_name'"
              :autocomplete="`family-name`"
              v-validate="'required|alpha_latin_dash|min:1|max:100'"
              data-vv-as="Last name"
              data-vv-scope="2"
              :error="errors.first('2.last_name')"
            />
          </div>
          <input-text
            v-model="formData.account.password"
            :label="`Password`"
            :name="'password'"
            :type="'password'"
            :autocomplete="`new-password`"
            v-validate="{
              required: true,
              min: 8,
              regex: passwordRegex,
            }"
            data-vv-as="Password"
            data-vv-scope="2"
            :error="errors.first('2.password')"
          />
        </div>
        <div class="sm-form__locations-btns">
          <btn-block
            v-show="activeStep > 0"
            :type="''"
            :classes="[
              'sm-btn--grey-fill',
              isValidateingFields ? 'disabled' : '',
            ]"
            :text="`Previous`"
            @click="goToStep(activeStep - 1, false)"
          />
          <btn-block
            v-show="activeStep < 2"
            :type="''"
            :classes="['right', isValidateingFields ? 'disabled' : '']"
            :text="`Next`"
            @click="goToStep(activeStep + 1)"
          />
          <btn-block
            v-show="activeStep === 2"
            :type="'submit'"
            :text="`Create account`"
            :classes="['sm-btn--submit', isValidateingFields ? 'disabled' : '']"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import InputText from "@/components/InputText";
import DropdownWrap from "@/components/DropdownWrap";
import FormDropdowns from "@/mixins/FormDropdowns";
import CountryDropdown from "@/components/CountryDropdown";
import StateDropdown from "@/components/StateDropdown";
import InputZip from "@/components/InputZip";
import PhoneInput from "@/components/PhoneInput";
import GeneralSettings from "@/settings";
import MimeTypes from "@/settings/mime-types";
import { IsRightFileType } from "@/utils/general-data";

export default {
  name: "add-new-location-form",
  components: {
    "dropdown-wrap": DropdownWrap,
    "input-text": InputText,
    "country-dropdown": CountryDropdown,
    "state-dropdown": StateDropdown,
    "input-zip": InputZip,
    "phone-input": PhoneInput,
  },
  data() {
    return {
      activeStep: 0,
      formData: {
        client: {
          name: "",
          subdomain: "",
          url: "https://",
          image: "",
          currency_id: "",
        },
        account: {
          user_name: "",
          first_name: "",
          last_name: "",
          password: "",
          company_name: "",
        },
        common: {
          email: "",
          phone: "",
          street: "",
          city: "",
          zip_code: "",
          country_id: null,
          state_id: null,
        },
      },
      sphereMailSubdomains: [
        "www",
        "support",
        "customer-support",
        "blog",
        "news",
      ],
      isValidating: {
        name: false,
        subdomain: false,
        user_name: false,
        email: false,
      },
      passwordRegex: GeneralSettings.passwordRegex,
      acceptTypes: "",
      imageName: "",
      currency: {
        id: 1,
        name: "USD",
        symbol: "$",
      },
    };
  },
  computed: {
    ...mapGetters({
      currencyList: "currencyList",
    }),
    isValidateingFields() {
      return Object.values(this.isValidating).some((val) => val);
    },
  },
  created() {
    this.acceptTypes = MimeTypes.simpleImage.join();
    this.currency = this.currencyList.find((item) => item.id === 1);
  },
  mixins: [FormDropdowns],
  methods: {
    ...mapActions({
      validateAccount: "account/validateAccount",
      validateClient: "account/validateClient",
    }),
    isRightFileType: IsRightFileType,
    uploadFile(e) {
      if (e.target.files.length === 0) return false;

      const target = e.target;
      const file = target.files[0];

      if (!this.isRightFileType(file, ["simpleImage"])) return false;

      this.imageName = file.name;
      this.formData.client.image = file;
      this.errors.remove("0.image");
    },
    validateDB(group, field, scope) {
      this.clearBackErrors();

      if (this.errors.first(field)) return;

      // check if subdomain is in sphereMail Subdomains
      if (
        field === "subdomain" &&
        this.sphereMailSubdomains.includes(this.formData.client[field])
      ) {
        this.errors.add({
          field,
          msg: `Subdomain "${this.formData["subdomain"]}" is not allowed!`,
        });
        return;
      }

      this.isValidating[field] = true;

      let validateAction = this.validateClient;
      if (field !== "subdomain" && field !== "name") {
        validateAction = this.validateAccount;
      }

      validateAction({
        [field]: this.formData[group][field],
      })
        .then(() => {
          this.isValidating[field] = false;
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.errors) {
            let err = e.response.data.errors;
            let msg = "";

            if (err.base) {
              msg = e.response.data.errors.base[0];
            } else {
              Object.keys(err).forEach((key) => {
                msg += `${key.toUpperCase()}: ${err[key][0]}\n`;
              });
            }

            this.errors.add({
              field: "backError_" + field,
              msg,
              scope,
            });
          }

          this.isValidating[field] = false;
        });
    },
    clearBackErrors() {
      if (this.errors.first("0.backError_name"))
        this.errors.remove("0.backError_name");
      if (this.errors.first("0.backError_subdomain"))
        this.errors.remove("0.backError_subdomain");
      if (this.errors.first("0.backError_email"))
        this.errors.remove("0.backError_email");
      if (this.errors.first("2.backError_user_name"))
        this.errors.remove("2.backError_user_name");
    },
    validate(scope, successCallback) {
      let actionsArr = [];
      if (scope === 0) {
        actionsArr = [
          this.validateDB("client", "name", `${scope}`),
          this.validateDB("client", "subdomain", `${scope}`),
          this.validateDB("common", "email", `${scope}`),
        ];
      } else if (scope === 2) {
        actionsArr = [this.validateDB("account", "user_name", `${scope}`)];
      }

      Promise.all(actionsArr).then(() => {
        if (scope === 0) {
          if (this.formData.client.image === "") {
            this.errors.add({
              field: "image",
              scope: "0",
              msg: "The 'Company Logo' field is required",
            });
          }

          if (!this.formData.common.phone) {
            this.errors.add({
              field: "manual_phone",
              scope: "0",
              msg: "The 'Phone number' field is required",
            });
          } else {
            this.errors.remove("0.manual_phone");
          }
        }
        this.$validator.validateAll(String(scope)).then((valid) => {
          if (!valid || this.errors.items.length) {
            this.goToStep(Number(this.errors.items[0].scope), false);
            return;
          }

          successCallback();
        });
      });
    },
    goToStep(step, validate = true) {
      if (step < 0 || step == this.activeStep || this.isValidateingFields)
        return;

      if (validate) {
        this.validate(this.activeStep, () => {
          this.activeStep = step;
        });
      } else {
        this.activeStep = step;
      }
    },

    handleSubmit() {
      this.validate(2, () => {
        this.$emit("submit", this.formData);
      });
    },
    cancel() {
      this.$emit("cancel");
    },
  },
  watch: {
    selectedCountry(val) {
      this.formData.common.country_id = val.id;
    },
    selectedState(val) {
      if (val) this.formData.common.state_id = val.id;
      else this.formData.common.state_id = "";
    },
    currency(val) {
      this.formData.client.currency_id = val.id;
    },
    "formData.client.name"(val) {
      this.formData.account.company_name = val;
    },
    "formData.common.email"() {
      this.clearBackErrors();
    },
    "formData.client.url"(val) {
      if (!val.startsWith("https://") && !val.startsWith("http://")) {
        this.formData.client.url = "https://" + val;
      }
    },
  },
};
</script>
